import { useEffect, useState, useCallback } from 'react'
import { jwtDecode } from 'jwt-decode'
import { refreshAccessToken } from '../lib/api/Oauth-Service'

const useTokenRefresh = () => {
  const [tokenExpiration, setTokenExpiration] = useState(null)

  const getStoredToken = useCallback(() => localStorage.getItem('access_token'), [])

  const clearTokenAndRedirect = useCallback(() => {
    localStorage.clear()
    window.location.href = '/login'
  }, [])

  const decodeToken = useCallback((token) => {
    try {
      return jwtDecode(token)
    } catch (error) {
      console.error('Failed to decode token:', error)
      return null
    }
  }, [])

  const refreshToken = useCallback(async () => {
    try {
      const response = await refreshAccessToken()
      const newToken = response.data
      localStorage.setItem('access_token', newToken)
      const decodedData = decodeToken(newToken)
      if (decodedData) {
        setTokenExpiration(decodedData.exp * 1000)
      }
    } catch (error) {
      console.error('Failed to refresh token:', error)
      clearTokenAndRedirect()
    }
  }, [decodeToken, clearTokenAndRedirect])

  useEffect(() => {
    const checkAndRefreshToken = () => {
      const token = getStoredToken()
      if (!token) return

      const decodedToken = decodeToken(token)
      if (!decodedToken) {
        clearTokenAndRedirect()
        return
      }

      const expiryTime = decodedToken.exp * 1000
      const currentTime = Date.now()
      const timeUntilExpiry = expiryTime - currentTime
      const refreshThreshold = 5 * 60 * 1000 // 5 minute

      if (timeUntilExpiry <= 0) {
        clearTokenAndRedirect()
      } else if (timeUntilExpiry <= refreshThreshold) {
        refreshToken()
      } else {
        const timerId = setTimeout(refreshToken, timeUntilExpiry - refreshThreshold)
        return () => clearTimeout(timerId)
      }
    }

    checkAndRefreshToken()
    const intervalId = setInterval(checkAndRefreshToken, 240000) // Check after every 4 minutes

    return () => clearInterval(intervalId)
  }, [getStoredToken, decodeToken, clearTokenAndRedirect, refreshToken])

  return tokenExpiration
}

export default useTokenRefresh